/**
 * src/components/spatial/spatialTooltipModel.ts
 *
 * What a hover card can honestly say about a spatial object without
 * asking anyone.
 *
 * ## The performance rule this file exists to enforce
 *
 * Dragging across a city hovers hundreds of buildings. If a hover card
 * fetched anything — a presence config, a Soroban balance, a treasury
 * status — the map would be a load generator pointed at our own API and
 * at the RPC. So the rule is absolute: **hovering costs nothing.** The
 * card is built from the properties already on the rendered feature,
 * which are already in the browser because they were needed to paint
 * the building.
 *
 * Anything that needs a network call belongs to the *pinned* card, the
 * one a deliberate click creates, and to it alone.
 *
 * ## Which is why some answers are "unknown"
 *
 * A viewport response is a summary: it may carry no metadata at all.
 * And whether an object will talk to you lives in its GeoLink presence
 * config, which is server-side and not mirrored onto the feature. A
 * card that guessed would be wrong on half the buildings in Dubai.
 *
 * So availability is three-valued, and unknown is a real answer that
 * gets rendered as one. The action stays offered when we cannot tell,
 * because the server is the authority and will say so itself — a
 * tooltip that hid Chat on a talkative building would be a worse lie
 * than one that offered it on a silent one.
 */

import { deriveEventState, type EventState } from './spatialFeatureDerivation';
import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';

/** Yes, no, or nobody has told us. */
export type Availability = 'available' | 'unavailable' | 'unknown';

/** A balance is never a bare number. It is always an amount of something. */
export interface TooltipAssetBalance {
  /** Canonical asset id — a SAC contract address, or a display code. */
  asset: string;
  /** Display code, when we have one. */
  assetCode: string | null;
  /** Stringified to avoid float rounding on a stroops amount. */
  amount: string | null;
}

/**
 * Treasury state on the hover card.
 *
 * Always optional and always passed in from outside. Nothing in this
 * module reads a chain: the hover card renders whatever the pinned card
 * previously loaded and cached, or nothing at all.
 */
export interface TooltipTreasury {
  balance: TooltipAssetBalance | null;
  canReceive: Availability;
  /** Compact indicator only. A hover card is not an admin screen. */
  hasBeneficiary: boolean;
  /** Why it cannot receive, when it cannot. */
  reason?: string | null;
}

export interface SpatialTooltipModel {
  objectId: string | null;
  name: string;
  objectType: string | null;
  typeLabel: string;
  /** Human place label, only if the feature already carried one. */
  place: string | null;
  countryCode: string | null;
  eventState: EventState | null;
  eventTitle: string | null;
  /** Whether the object will hold a conversation. */
  chat: Availability;
  /** Whether there is live audio to listen to. */
  listen: Availability;
  treasury: TooltipTreasury | null;
  /** The stripe down the card's left edge. */
  accentColor: string;
}

/** Matches the event colours the extrusion layer already paints. */
const LIVE_COLOR = '#22c55e';
const SOON_COLOR = '#f59e0b';
const DEFAULT_ACCENT = '#6366f1';

const TYPE_LABELS: Record<string, string> = {
  building: 'Building',
  floor: 'Floor',
  zone: 'Zone',
  slice: 'Slice',
  point: 'Point',
  poi: 'Place',
};

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function bag(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  // Mapbox stringifies nested properties when a feature comes back
  // through `queryRenderedFeatures`.
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * A short place label, if one is already on the feature.
 *
 * Deliberately not reverse-geocoded. The server does that for the AI's
 * context and caches it per centroid; doing it here would mean a
 * geocoding request per building hovered, which is the exact thing this
 * module exists to prevent.
 */
function placeFrom(meta: Record<string, unknown>): string | null {
  const address = text(meta.address) || text(meta.formatted_address) || text(meta.street);
  const locality = text(meta.locality) || text(meta.city) || text(meta.place);
  if (address && locality && !address.includes(locality)) return `${address}, ${locality}`;
  return address || locality || null;
}

/**
 * Whether this object talks.
 *
 * Read from a metadata mirror rather than the presence config, for the
 * same reason the flag is mirrored: neither the viewport response nor
 * the render envelope carries presence config, and fetching it per
 * visible building is not an option. An object whose metadata says
 * nothing is `unknown`, not `unavailable`.
 */
function chatFrom(meta: Record<string, unknown>, props: Record<string, unknown>): Availability {
  const presence = bag(meta.ai_presence ?? meta.aiPresence);
  if (typeof presence.enabled === 'boolean') {
    if (!presence.enabled) return 'unavailable';
    // A present-but-passive object is present, not conversational.
    return text(presence.mode ?? presence.interactionMode) === 'passive'
      ? 'unavailable'
      : 'available';
  }

  const verbs = Array.isArray(props.interactionTypes) ? props.interactionTypes : [];
  if (verbs.some((v) => typeof v === 'string' && /chat|converse|ask/i.test(v))) {
    return 'available';
  }
  return 'unknown';
}

/**
 * Whether there is something to hear right now.
 *
 * `liveAudio` is the real answer when we have it: a single poll of the
 * active-broadcast list tells the whole map which objects are speaking,
 * so this does not have to infer it (see `useLiveBroadcasts`). That
 * matters because the two facts genuinely differ — an object can be
 * mid-broadcast with no occasion recorded, and can be inside a scheduled
 * occasion while saying nothing at all.
 *
 * Without that answer we fall back to the occasion window, which is a
 * guess, and say `unknown` rather than `unavailable` so the action stays
 * offered. The card that opens will find out properly.
 */
function listenFrom(
  meta: Record<string, unknown>,
  eventLive: boolean,
  liveAudio: boolean | null,
): Availability {
  const media = bag(meta.media_presence ?? meta.mediaPresence);
  if (typeof media.enabled === 'boolean' && !media.enabled) return 'unavailable';
  if (liveAudio !== null) return liveAudio ? 'available' : 'unavailable';
  return eventLive ? 'available' : 'unknown';
}

/**
 * Build the hover card's contents from a rendered feature.
 *
 * Pure, and pure on purpose: everything here can be checked without a
 * map, a canvas or a network, which is the only way to be confident
 * that hovering stayed free.
 */
export function deriveSpatialTooltipModel(
  feature: SpatialObjectFeature | null,
  options: {
    now?: number;
    treasury?: TooltipTreasury | null;
    /**
     * Whether this object is broadcasting, when that is known. `null`
     * means nobody has asked yet — which is not the same as "no".
     */
    liveAudio?: boolean | null;
  } = {},
): SpatialTooltipModel | null {
  if (!feature) return null;
  const props = (feature.properties || {}) as Record<string, unknown>;
  const meta = bag(props.metadata);
  const now = options.now ?? Date.now();

  const objectType = text(props.objectType);
  const typeLabel = (objectType && TYPE_LABELS[objectType]) || 'Object';

  const name = text(props.displayName)
    || text(props.name)
    || text(meta.name)
    || text(meta.title)
    || text(meta.label)
    // Never blank: an unnamed building is still a building.
    || typeLabel;

  // Prefer the state already stamped on the feature — it was derived
  // once for the paint expressions — and fall back to deriving it, so a
  // feature that arrived from somewhere else still reads correctly.
  const stamped = text(props.eventState);
  const eventState = (stamped === 'live' || stamped === 'soon' || stamped === 'past')
    ? stamped as EventState
    : deriveEventState(meta, now) ?? null;

  const event = bag(meta.event);
  const live = eventState === 'live';

  return {
    objectId: text(props.objectId),
    name,
    objectType,
    typeLabel,
    place: placeFrom(meta),
    countryCode: text(props.countryCode) || text(props.flagCountryCode),
    eventState,
    eventTitle: text(event.title) || text(event.name),
    chat: chatFrom(meta, props),
    listen: listenFrom(meta, live, options.liveAudio ?? null),
    treasury: options.treasury ?? null,
    accentColor: eventState === 'live'
      ? LIVE_COLOR
      : eventState === 'soon'
        ? SOON_COLOR
        : text(props.colorKey) || text(props.fill_color) || DEFAULT_ACCENT,
  };
}

/** How the card words an event's state. */
export function eventStateLabel(state: EventState | null): string | null {
  switch (state) {
    case 'live': return 'Live now';
    case 'soon': return 'Starting soon';
    case 'past': return 'Finished';
    default: return null;
  }
}
